import React, { useState, useEffect, useRef } from 'react';
import Markdown from 'react-markdown';
import gfm from 'remark-gfm';
import highlight from 'rehype-highlight';
import 'highlight.js/styles/github.css';
import 'highlight.js/styles/atom-one-dark.css';
import rehypeRaw from 'rehype-raw';
import { visit } from 'unist-util-visit';
import { CopyAll, Done } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface TypingTextProps {
    text: string;
}

const TypingText: React.FC<TypingTextProps> = ({ text }) => {
    const [displayedText, setDisplayedText] = useState('');
    const textRef = useRef<string>(text);
    const intervalRef = useRef<number | null>(null);
    const [index, setIndex] = useState(0);
    const [copyed, setCopyed] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        textRef.current = text;
        setIndex(0);
    }, [text]);

    useEffect(() => {
        if (index < textRef.current.length) {
            intervalRef.current = requestAnimationFrame(() => {
                setDisplayedText(textRef.current.substring(0, index + 1));
                setIndex(index + 1);
            });
        } else if (intervalRef.current !== null) {
            cancelAnimationFrame(intervalRef.current);
        }
        return () => {
            if (intervalRef.current !== null) {
                cancelAnimationFrame(intervalRef.current);
            }
        };
    }, [index]);

    function extractPlainText(jsonData: any) {
        let text = "";
        function traverse(node: any) {
            if (node.type === "text") {
                text += node.value;
            } else if (node.children) {
                node.children.forEach(traverse);
            }
        }
        traverse(jsonData);
        return text;
    }


    const handleCopyCode = (code: string) => {
        try {
            const textArea = document.createElement('textarea');
            textArea.value = code;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopyed(true);
            setTimeout(() => {
                setCopyed(false);
            }, 3000);
            // alert('Code copié dans le presse-papiers !');
        } catch (error) {
            alert(t("copyError"));
        }


    };


    const addCopyButtons = () => {
        return (tree: any) => {
            visit(tree, 'element', (node: any) => {
                if (node.tagName === 'pre' && node.children[0].tagName === 'code') {
                    const codeNode = node.children[0];
                    const button = {
                        type: 'element',
                        tagName: 'button',
                        properties: {
                            className: ['copy-button'],
                            // style: 'position: absolute; top: 10%; right: 2%;',
                            onclick: () => {
                                handleCopyCode(extractPlainText(codeNode));
                            }
                        },
                        children: [{ type: 'text', value: <Box gap={1} display="flex"><label htmlFor="">{t("copy")} </label> {copyed ? <Done fontSize='small' /> : <CopyAll fontSize='small' />} </Box> }]
                    };
                    node.properties = node.properties || {};
                    node.properties.style = 'position: relative;';
                    node.children.push(button);
                }
            });
        };
    };

    return (
        <Markdown
            remarkPlugins={[gfm]}
            rehypePlugins={[highlight, rehypeRaw, addCopyButtons]}
        >
            {displayedText}
        </Markdown>
    );
};

export default TypingText;